import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertMessageInputProps } from './alertMessageInput.typings'
import { selectIndicators } from '../../../selectors/chart'
import { getUserAlertMessageFromCondition } from '../../../util/alerts'
import { useSelector } from 'react-redux'
import { TextField } from '@mui/material'

const AlertMessageInput: React.FC<AlertMessageInputProps> = ({
                                                                 condition,
                                                                 symbolDisplayName,
                                                                 message,
                                                                 onChange,
                                                                 className,
                                                                 edit,
                                                                 chartSnapshot,
                                                                 chartTimeBucket,
                                                             }) => {
    const indicators = useSelector(selectIndicators)
    const manualInput = useRef(false)
    const [isAutoUpdatable, setIsAutoUpdatable] = useState(false)
    const initialRender = useRef(true)
    const prevConditionJsonRef = useRef<string | null>(null)
    const messageContext = useMemo(
        () => ({ chartSnapshot, chartTimeBucket: chartTimeBucket ?? chartSnapshot?.timeBucket }),
        [chartSnapshot, chartTimeBucket]
    )

    useEffect(() => {
        if (!edit || !initialRender.current) return
        let cancelled = false
        void (async () => {
            const generatedMessage = await getUserAlertMessageFromCondition(
                condition,
                symbolDisplayName,
                indicators,
                messageContext
            )
            if (cancelled) return
            setIsAutoUpdatable(message === generatedMessage)
            initialRender.current = false
        })()
        return () => {
            cancelled = true
        }
    }, [edit, message, condition, symbolDisplayName, indicators, messageContext])

    useEffect(() => {
        const condJson = JSON.stringify(condition ?? null)
        const conditionChanged =
            prevConditionJsonRef.current !== null && prevConditionJsonRef.current !== condJson
        let cancelled = false
        void (async () => {
            if (conditionChanged) {
                manualInput.current = false
                const next = await getUserAlertMessageFromCondition(
                    condition,
                    symbolDisplayName,
                    indicators,
                    messageContext
                )
                if (cancelled) return
                onChange(next)
                if (edit) setIsAutoUpdatable(true)
            }

            if (!manualInput.current && !conditionChanged) {
                const next = await getUserAlertMessageFromCondition(
                    condition,
                    symbolDisplayName,
                    indicators,
                    messageContext
                )
                if (cancelled) return
                if (edit && isAutoUpdatable && message !== next) {
                    onChange(next)
                } else if (!edit && message !== next) {
                    onChange(next)
                }
            }
        })()
        prevConditionJsonRef.current = condJson
        return () => {
            cancelled = true
        }
    }, [
        condition,
        indicators,
        symbolDisplayName,
        message,
        onChange,
        edit,
        isAutoUpdatable,
        messageContext,
    ])

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        manualInput.current = true
        setIsAutoUpdatable(false)
        onChange(event.target.value)
    }, [onChange])

    return (
        <TextField
            fullWidth
            multiline
            size="small"
            minRows={2}
            value={message}
            className={className}
            onChange={onInputChange}
        />
    )
}

export default memo(AlertMessageInput)
